import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AnimatePresence } from "framer-motion";

import { LanguageProvider } from "./lib/i18n";
import { SiteProvider, useSite } from "./context/SiteContext";
import { BookingProvider } from "./context/BookingContext";
import { AuthProvider } from "./context/AuthContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import BrandLoader from "./components/BrandLoader";
import Analytics from "./components/Analytics";
import CookieConsent from "./components/CookieConsent";
import AmbientSound from "./components/AmbientSound";
import FloatingContact from "./components/FloatingContact";

import Home from "./pages/Home";
import Excursions from "./pages/Excursions";
import ExcursionDetail from "./pages/ExcursionDetail";
import MapPage from "./pages/MapPage";
import Places from "./pages/Places";
import Seasons from "./pages/Seasons";
import SeasonPage from "./pages/SeasonPage";
import GuidePage from "./pages/GuidePage";
import GuideStories from "./pages/GuideStories";
import AboutAltai from "./pages/AboutAltai";
import Contacts from "./pages/Contacts";
import Reviews from "./pages/Reviews";
import CustomTour from "./pages/CustomTour";
import Credits from "./pages/Credits";
import FaqPage from "./pages/FaqPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import PersonalDataConsent from "./pages/PersonalDataConsent";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import AdminSchedule, { AdminScheduleShortcut } from "./pages/AdminSchedule";
import AdminGuideJournal, { GuideJournalShortcut } from "./pages/AdminGuideJournal";
import NotFound from "./pages/NotFound";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const SmoothScroll = () => {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
};

const Layout = ({ children }) => {
  const site = useSite();
  const contacts = site?.settings?.contacts;
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer contacts={contacts} />
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(() => !sessionStorage.getItem("argali_entered"));

  useEffect(() => {
    if (!loading) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => {
      sessionStorage.setItem("argali_entered", "1");
      setLoading(false);
    }, reduce ? 500 : 2000);
    return () => clearTimeout(t);
  }, [loading]);

  return (
    <div className="App">
      <AnimatePresence>{loading && <BrandLoader />}</AnimatePresence>
      <LanguageProvider>
        <SiteProvider>
          <AuthProvider>
            <BookingProvider>
              <BrowserRouter>
                <ScrollToTop />
                <SmoothScroll />
                <Analytics />
                <CookieConsent />
                <AmbientSound />
                <FloatingContact />
                <Toaster position="top-center" theme="dark" richColors />
                <Routes>
                  <Route path="/" element={<Layout><Home /></Layout>} />
                  <Route path="/excursions" element={<Layout><Excursions /></Layout>} />
                  <Route path="/excursions/:slug" element={<Layout><ExcursionDetail /></Layout>} />
                  <Route path="/map" element={<Layout><MapPage /></Layout>} />
                  <Route path="/places" element={<Layout><Places /></Layout>} />
                  <Route path="/seasons" element={<Layout><Seasons /></Layout>} />
                  <Route path="/seasons/:season" element={<Layout><SeasonPage /></Layout>} />
                  <Route path="/guide" element={<Layout><GuidePage /></Layout>} />
                  <Route path="/guide/stories" element={<Layout><GuideStories /></Layout>} />
                  <Route path="/altai" element={<Layout><AboutAltai /></Layout>} />
                  <Route path="/contacts" element={<Layout><Contacts /></Layout>} />
                  <Route path="/reviews" element={<Layout><Reviews /></Layout>} />
                  <Route path="/custom-tour" element={<Layout><CustomTour /></Layout>} />
                  <Route path="/credits" element={<Layout><Credits /></Layout>} />
                  <Route path="/faq" element={<Layout><FaqPage /></Layout>} />
                  <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
                  <Route path="/terms" element={<Layout><Terms /></Layout>} />
                  <Route path="/personal-data-consent" element={<Layout><PersonalDataConsent /></Layout>} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin" element={<><Admin /><AdminScheduleShortcut /><GuideJournalShortcut /></>} />
                  <Route path="/admin/schedule" element={<AdminSchedule />} />
                  <Route path="/admin/journal" element={<AdminGuideJournal />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </BookingProvider>
          </AuthProvider>
        </SiteProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;